<template>
  <div class="shell">

    <!-- Background texture -->
    <div class="bg-texture" aria-hidden="true"></div>

    <div class="wordmark">
      <hr-payroll-mark variant="full" />
    </div>

    <main class="card">

      <div v-if="bootstrapping" class="boot-panel">
        <span class="spinner-border text-primary" role="status" aria-hidden="true"></span>
        <p class="mb-0 mt-3">Setting up your workspace…</p>
      </div>

      <template v-else>
      <!-- Left: action panel -->
      <section class="panel-left">
        <div class="envelope-icon" aria-hidden="true">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="10" fill="#0c0f1a"/>
            <path d="M7 11.5L16 17.5L25 11.5" stroke="#f5c97a" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <rect x="7" y="10" width="18" height="13" rx="2" stroke="white" stroke-width="1.5"/>
          </svg>
        </div>

        <div class="eyebrow">Email Verification</div>
        <h1>Check your<br><em>inbox.</em></h1>

        <p class="lead">
          We sent a verification link to
          <span class="email-chip">{{ auth.user?.email || "your email" }}</span>.
          Open it, click the link, then come back here to continue.
        </p>

        <!-- Steps -->
        <ol class="steps" aria-label="Verification steps">
          <li class="step">
            <span class="step-num" aria-hidden="true">1</span>
            <span>Open the email we sent after signup.</span>
          </li>
          <li class="step">
            <span class="step-num" aria-hidden="true">2</span>
            <span>Click the verification link inside.</span>
          </li>
          <li class="step">
            <span class="step-num" aria-hidden="true">3</span>
            <span>Return here and continue setup.</span>
          </li>
        </ol>

        <!-- Feedback banners -->
        <Transition name="banner">
          <div v-if="message" class="banner banner-ok" role="status">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8.5L6.5 12L13 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
            {{ message }}
          </div>
        </Transition>
        <Transition name="banner">
          <div v-if="error" class="banner banner-err" role="alert">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 4.5V8.5M8 11H8.01" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
            {{ error }}
          </div>
        </Transition>

        <!-- Actions -->
        <div class="actions">
          <button class="btn btn-primary" :disabled="checking" @click="continueAfterVerification">
            <span v-if="checking" class="spinner" aria-hidden="true"></span>
            <span>{{ checking ? "Checking…" : "I've verified my email" }}</span>
            <svg v-if="!checking" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2.5 7H11.5M7.5 3L11.5 7L7.5 11" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>

          <button class="btn btn-ghost" :disabled="resending" @click="resendEmail">
            <span v-if="resending" class="spinner spinner-dark" aria-hidden="true"></span>
            <span>{{ resending ? "Sending…" : "Resend email" }}</span>
          </button>

          <router-link class="btn-link" to="/login">← Back to login</router-link>
        </div>
      </section>

      <!-- Right: info panel -->
      <aside class="panel-right" aria-label="What happens after verification">
        <div class="aside-tag">What comes next</div>
        <h2>Your path to a<br>verified workspace.</h2>
        <p class="aside-sub">Confirming your email unlocks the full onboarding flow — billing, team invites, and module setup.</p>

        <div class="timeline">
          <div class="tl-item">
            <div class="tl-dot tl-active"></div>
            <div class="tl-content">
              <strong>Verify your email</strong>
              <span>Opens access to the rest of onboarding.</span>
            </div>
          </div>
          <div class="tl-item">
            <div class="tl-dot"></div>
            <div class="tl-content">
              <strong>Select a plan</strong>
              <span>Choose the modules your company needs.</span>
            </div>
          </div>
          <div class="tl-item">
            <div class="tl-dot"></div>
            <div class="tl-content">
              <strong>Configure &amp; billing</strong>
              <span>Add your team, set permissions, and go live.</span>
            </div>
          </div>
        </div>

        <div class="aside-note">
          Account suspended? Restore access by resolving your billing from the billing portal.
        </div>
      </aside>
      </template>

    </main>

    <p class="footer">© {{ currentYear }} DoneQuick. All rights reserved.</p>
  </div>
</template>

<script setup>
import HrPayrollMark from "@/components/branding/hr-payroll-mark.vue";
import { computed, onMounted, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/stores/authStore";
import { useAccessStore } from "@/stores/accessStore";
import AuthService from "@/services/authService";
import api from "@/services/apiService";
import { isTenancyFrontendEnabled, isTenantHost } from "@/utils/hostname";
import { toast } from "@/utils/toast";

/** One exchange per token across Vue dev double-mounts / duplicate component instances. */
let activeOnboardingExchange = null;

const router = useRouter();
const route  = useRoute();
const auth   = useAuthStore();
const access = useAccessStore();

const checking  = ref(false);
const resending = ref(false);
const bootstrapping = ref(
  Boolean(route.query.onboarding) ||
    (route.query.confirm === "1" && route.query.id && route.query.hash)
);
const message   = ref(route.query.verified ? "Email verified. You can continue now." : "");
const error     = ref("");
const currentYear = new Date().getFullYear();

const selectedPlanTarget = computed(() => {
  const planId = route.query.plan_id;
  return planId ? `/select-plan?plan_id=${planId}` : "/select-plan";
});

const postVerifyDashboard = computed(() => {
  const roles = auth.user?.roles || [];
  const isSuperAdmin = roles.includes("SuperAdmin");
  const isPureEmployee =
    roles.includes("Employee") &&
    !roles.includes("Admin") &&
    !roles.includes("Hr") &&
    !roles.includes("HR") &&
    !isSuperAdmin;

  return isSuperAdmin
    ? "/super-admin/dashboard"
    : isPureEmployee
    ? "/dashboard/employee-dashboard"
    : "/dashboard/admin-dashboard";
});

async function resolveNextRoute() {
  await auth.fetchCurrentUser();

  if (!auth.user?.email_verified_at) {
    return null;
  }

  await access.fetchAccess({ force: true });

  if (access.planId) {
    const needsBilling =
      access.paymentStatus === "unpaid" && access.status !== "trialing";

    return needsBilling ? "/billing" : postVerifyDashboard.value;
  }

  return selectedPlanTarget.value;
}

async function continueAfterVerification() {
  checking.value = true;
  error.value    = "";
  try {
    const nextRoute = await resolveNextRoute();
    if (!nextRoute) {
      error.value = "Your email isn't verified yet — please use the link in your inbox.";
      return;
    }
    await router.push(nextRoute);
  } finally {
    checking.value = false;
  }
}

async function resendEmail() {
  resending.value = true;
  error.value     = "";
  message.value   = "";
  try {
    const res     = await AuthService.resendVerificationEmail();
    message.value = res?.data?.message || "Verification email sent.";
    toast(message.value);
  } catch (e) {
    error.value = e?.response?.data?.message || "Failed to resend the verification email.";
    toast(error.value, "error");
  } finally {
    resending.value = false;
  }
}

async function stripOnboardingQuery() {
  if (!route.query.onboarding) return;

  const nextQuery = { ...route.query };
  delete nextQuery.onboarding;

  await router
    .replace({
      path: "/auth/verify-email",
      query: Object.keys(nextQuery).length ? nextQuery : {},
    })
    .catch(() => {});
}

function restoreSessionFromStorage() {
  if (auth.isAuthenticated) {
    return true;
  }

  const storedToken = localStorage.getItem("token");
  const storedUser = localStorage.getItem("user");
  if (!storedToken || !storedUser) {
    return false;
  }

  try {
    const parsedUser = JSON.parse(storedUser);
    if (!parsedUser || typeof parsedUser !== "object") {
      return false;
    }
    auth.setSession(storedToken, parsedUser);
    return true;
  } catch {
    return false;
  }
}

async function exchangeOnboardingToken(token) {
  if (restoreSessionFromStorage()) {
    await stripOnboardingQuery();
    return;
  }

  if (activeOnboardingExchange) {
    return activeOnboardingExchange;
  }

  activeOnboardingExchange = (async () => {
    const res = await Promise.race([
      api.post("onboarding/exchange", { token }),
      new Promise((_, reject) => {
        window.setTimeout(
          () => reject(new Error("Workspace setup timed out. Please try logging in.")),
          20000
        );
      }),
    ]);

    if (!res?.data?.token || !res?.data?.user) {
      throw new Error("Onboarding exchange returned an invalid session.");
    }

    auth.setSession(res.data.token, res.data.user);
    await stripOnboardingQuery();
  })();

  try {
    await activeOnboardingExchange;
  } finally {
    activeOnboardingExchange = null;
  }
}

async function confirmEmailFromLink() {
  const { id, hash, expires, signature } = route.query;
  await api.get(`email/verify/${id}/${hash}`, {
    expires,
    signature,
  });

  if (auth.isAuthenticated) {
    await auth.fetchCurrentUser?.();
  }

  message.value = "Email verified. You can continue now.";
  const nextQuery = { verified: "1" };
  if (route.query.plan_id) {
    nextQuery.plan_id = route.query.plan_id;
  }
  await router
    .replace({
      path: "/auth/verify-email",
      query: nextQuery,
    })
    .catch(() => {});
}

onMounted(async () => {
  error.value = "";
  window.dismissBootSplash?.();

  try {
    const onboardingToken = route.query.onboarding;
    if (onboardingToken && isTenancyFrontendEnabled() && isTenantHost()) {
      try {
        await exchangeOnboardingToken(onboardingToken);
      } catch (exchangeError) {
        if (restoreSessionFromStorage()) {
          await stripOnboardingQuery();
        } else if (exchangeError?.response?.status === 403) {
          error.value =
            "This setup link was already used or has expired. Log in with the email and password you created during signup.";
          await stripOnboardingQuery();
        } else {
          error.value =
            exchangeError?.response?.data?.message ||
            exchangeError?.message ||
            "Could not start your workspace session. Please try logging in.";
          await stripOnboardingQuery();
        }
      }
    }

    if (route.query.confirm === "1" && route.query.id && route.query.hash) {
      await confirmEmailFromLink();
    }
  } catch (e) {
    if (!auth.isAuthenticated) {
      error.value =
        e?.response?.data?.message ||
        e?.message ||
        "Could not start your workspace session. Please try registering again.";
    }
  } finally {
    bootstrapping.value = false;
    window.dismissBootSplash?.();
  }

  if (!route.query.verified) return;

  checking.value = true;
  try {
    const nextRoute = await resolveNextRoute();
    if (nextRoute) {
      await router.replace(nextRoute);
    }
  } finally {
    checking.value = false;
  }
});
</script>

<style scoped>
.boot-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  padding: 2rem;
  text-align: center;
  color: var(--ink-2, #3b4261);
  grid-column: 1 / -1;
}

/* ── Tokens ───────────────────────────── */
.shell {
  --ink:    #0c0f1a;
  --ink-2:  #3b4261;
  --ink-3:  #8891b0;
  --paper:  #f6f5f1;
  --card:   #ffffff;
  --line:   rgba(12,15,26,.08);
  --ok:     #1a6b45;
  --ok-bg:  #e6f4ed;
  --err:    #9b1c1c;
  --err-bg: #fde8e8;
  --gold:   #c9933a;
  --gold-2: #f5c97a;
}

/* ── Shell ────────────────────────────── */
.shell {
  position: relative;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  background-color: var(--paper);
  font-family: 'DM Sans', 'Segoe UI', system-ui, sans-serif;
  color: var(--ink);
}

.bg-texture {
  position: fixed;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%230c0f1a' fill-opacity='0.028'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E");
  pointer-events: none;
  z-index: 0;
}

/* ── Wordmark ─────────────────────────── */
.wordmark {
  position: relative;
  z-index: 1;
  margin-bottom: 28px;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: fade-up .45s ease both;
}
.wordmark :deep(.dq-brand-mark) {
  color: var(--ink-2);
}

/* ── Card ─────────────────────────────── */
.card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 840px;
  background: var(--card);
  border-radius: 32px;
  border: 1px solid var(--line);
  box-shadow:
    0 2px 4px rgba(12,15,26,.04),
    0 12px 40px rgba(12,15,26,.09),
    0 40px 80px rgba(12,15,26,.06);
  display: grid;
  grid-template-columns: 1.1fr .9fr;
  overflow: hidden;
  animation: fade-up .5s .08s ease both;
}

/* ── Left panel ───────────────────────── */
.panel-left {
  padding: 48px;
  display: flex;
  flex-direction: column;
  gap: 0;
  border-right: 1px solid var(--line);
}

.envelope-icon {
  margin-bottom: 22px;
  animation: fade-up .45s .16s ease both;
}

.eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 14px;
}

h1 {
  font-size: clamp(32px, 4.5vw, 46px);
  font-weight: 700;
  line-height: 1.04;
  letter-spacing: -.025em;
  margin-bottom: 20px;
  color: var(--ink);
}
h1 em {
  font-style: italic;
  font-weight: 300;
  color: var(--gold);
}

.lead {
  font-size: 15px;
  line-height: 1.75;
  color: var(--ink-2);
  margin-bottom: 28px;
}

.email-chip {
  display: inline-block;
  background: rgba(12,15,26,.06);
  border: 1px solid rgba(12,15,26,.10);
  border-radius: 6px;
  padding: 1px 7px;
  font-weight: 600;
  font-size: 13.5px;
  color: var(--ink);
  word-break: break-all;
}

/* ── Steps ────────────────────────────── */
.steps {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
}
.step {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 16px;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: var(--paper);
  font-size: 14px;
  font-weight: 500;
  color: var(--ink-2);
}
.step-num {
  flex-shrink: 0;
  width: 24px; height: 24px;
  border-radius: 7px;
  background: var(--ink);
  color: var(--gold-2);
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Banners ──────────────────────────── */
.banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  border-radius: 12px;
  font-size: 13.5px;
  font-weight: 500;
  margin-bottom: 16px;
}
.banner-ok  { background: var(--ok-bg);  color: var(--ok); }
.banner-err { background: var(--err-bg); color: var(--err); }

.banner-enter-active, .banner-leave-active { transition: opacity .2s, transform .2s; }
.banner-enter-from, .banner-leave-to { opacity: 0; transform: translateY(-6px); }

/* ── Actions ──────────────────────────── */
.actions {
  margin-top: auto;
  padding-top: 8px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 46px;
  padding: 0 22px;
  border-radius: 13px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  text-decoration: none;
  transition: transform .15s ease, box-shadow .15s ease, opacity .15s ease;
}
.btn:disabled { opacity: .55; cursor: not-allowed; transform: none !important; }
.btn:not(:disabled):hover { transform: translateY(-1px); }
.btn:not(:disabled):active { transform: translateY(0); }

.btn-primary {
  background: var(--ink);
  color: #fff;
  box-shadow: 0 4px 16px rgba(12,15,26,.22);
}
.btn-primary:not(:disabled):hover { box-shadow: 0 8px 24px rgba(12,15,26,.30); }
.btn-primary svg { transition: transform .15s ease; }
.btn-primary:not(:disabled):hover svg { transform: translateX(3px); }

.btn-ghost {
  background: transparent;
  color: var(--ink-2);
  border: 1.5px solid var(--line);
}
.btn-ghost:not(:disabled):hover {
  color: var(--ink);
  border-color: rgba(12,15,26,.20);
}

.btn-link {
  font-size: 13.5px;
  font-weight: 500;
  color: var(--ink-3);
  text-decoration: none;
  padding: 4px 0;
  transition: color .15s ease;
}
.btn-link:hover { color: var(--ink-2); }

/* Spinner */
.spinner {
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin .65s linear infinite;
  flex-shrink: 0;
}
.spinner-dark {
  border-color: rgba(12,15,26,.15);
  border-top-color: var(--ink-2);
}

/* ── Right panel ──────────────────────── */
.panel-right {
  padding: 48px 42px;
  background: var(--ink);
  color: #fff;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}
.panel-right::before {
  content: '';
  position: absolute;
  width: 300px; height: 300px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(201,147,58,.2), transparent 68%);
  top: -60px; right: -60px;
  pointer-events: none;
}

.aside-tag {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: .2em;
  text-transform: uppercase;
  color: var(--gold-2);
  margin-bottom: 18px;
}

.panel-right h2 {
  font-size: clamp(20px, 2.8vw, 28px);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -.02em;
  margin-bottom: 14px;
}

.aside-sub {
  font-size: 13.5px;
  line-height: 1.75;
  color: rgba(255,255,255,.55);
  margin-bottom: 36px;
}

/* ── Timeline ─────────────────────────── */
.timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
}
.timeline::before {
  content: '';
  position: absolute;
  left: 7px; top: 8px; bottom: 8px;
  width: 1px;
  background: rgba(255,255,255,.1);
}

.tl-item {
  display: flex;
  gap: 18px;
  padding-bottom: 24px;
  position: relative;
  z-index: 1;
}
.tl-item:last-child { padding-bottom: 0; }

.tl-dot {
  flex-shrink: 0;
  width: 15px; height: 15px;
  border-radius: 50%;
  margin-top: 3px;
  background: rgba(255,255,255,.15);
  border: 1.5px solid rgba(255,255,255,.18);
}
.tl-dot.tl-active {
  background: var(--gold);
  border-color: var(--gold);
  box-shadow: 0 0 0 4px rgba(201,147,58,.25);
}

.tl-content strong {
  display: block;
  font-size: 13.5px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 3px;
}
.tl-content span {
  font-size: 12.5px;
  color: rgba(255,255,255,.48);
  line-height: 1.55;
}

.aside-note {
  margin-top: auto;
  padding-top: 28px;
  font-size: 12px;
  color: rgba(255,255,255,.38);
  line-height: 1.6;
  border-top: 1px solid rgba(255,255,255,.08);
}

/* ── Footer ───────────────────────────── */
.footer {
  position: relative;
  z-index: 1;
  margin-top: 22px;
  font-size: 12px;
  color: var(--ink-3);
  animation: fade-up .5s .2s ease both;
}

/* ── Keyframes ────────────────────────── */
@keyframes fade-up {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── Responsive ───────────────────────── */
@media (max-width: 680px) {
  .card { grid-template-columns: 1fr; }
  .panel-left { padding: 32px 24px; border-right: none; border-bottom: 1px solid var(--line); }
  .panel-right { padding: 32px 24px; }
}
</style>
